import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IonSegment,
  IonSegmentButton,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonList,
  IonItem,
  IonLabel,
  IonContent,
  LoadingController,
  RefresherCustomEvent,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  IonFab,
  IonFabButton,
  IonIcon,
  IonButton,
  IonSearchbar,
  IonAvatar,
} from '@ionic/angular/standalone';
import { CharactersService } from 'src/app/features/characters/characters.service';
import { SupabaseService } from 'src/app/features/supabase/supabase.service';
import { RouterLink } from '@angular/router';
import { TRANSLATE_IMPORTS } from 'src/app/features/i18n/translate-imports';
import { libraryCampaignLabel } from 'src/app/features/characters/utils/characterStoryLinks';
import { LibraryCharacter } from 'src/app/features/characters/types/character';

@Component({
  selector: 're-characters',
  templateUrl: './characters.page.html',
  styleUrls: ['./characters.page.scss'],
  imports: [
    IonList,
    IonItem,
    IonLabel,
    IonIcon,
    IonButton,
    IonSearchbar,
    IonAvatar,
    IonFabButton,
    IonFab,
    IonSkeletonText,
    IonRefresherContent,
    IonRefresher,
    IonContent,
    IonTitle,
    IonToolbar,
    IonHeader,
    CommonModule,
    IonSegment,
    IonSegmentButton,
    RouterLink,
    ...TRANSLATE_IMPORTS,
  ],
})
export class CharactersPage {
  charactersService = inject(CharactersService);
  supabase = inject(SupabaseService);
  loader = inject(LoadingController);
  segment = signal<'sheets' | 'templates'>('sheets');
  search = signal('');

  myCharacters = this.charactersService.myLibrarySheets.data;
  filteredCharacters = computed(() =>
    this.filterLibrarySheets(this.myCharacters() ?? []),
  );

  handleRefreshMyCharacters(event: RefresherCustomEvent) {
    this.charactersService.myLibrarySheets.refetch().then(() => {
      event.target.complete();
    });
  }

  myTemplates = this.charactersService.myTemplates.data;
  filteredTemplates = computed(() =>
    this.filterAndSort(this.myTemplates() ?? []),
  );

  handleRefreshMyTemplates(event: RefresherCustomEvent) {
    this.charactersService.myTemplates.refetch().then(() => {
      event.target.complete();
    });
  }

  onSegmentChange(ev: Event) {
    const v = (ev as CustomEvent<{ value?: string }>).detail?.value;
    if (v === 'sheets' || v === 'templates') {
      this.segment.set(v);
      this.search.set('');
    }
  }

  onSearch(event: Event) {
    const value = (event as CustomEvent<{ value?: string }>).detail?.value ?? '';
    this.search.set(value);
  }

  campaignLabel(character: LibraryCharacter): string {
    return libraryCampaignLabel(character.storyLinks);
  }

  avatarInitial(name: string): string {
    const trimmed = name?.trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
  }

  private filterLibrarySheets(items: LibraryCharacter[]): LibraryCharacter[] {
    const query = this.search().trim().toLocaleLowerCase();
    return [...items]
      .filter((item) => {
        if (!query) return true;
        if (item.name.toLocaleLowerCase().includes(query)) return true;
        return item.storyLinks.some((story) =>
          story.name.toLocaleLowerCase().includes(query),
        );
      })
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      );
  }

  private filterAndSort<T extends { name: string }>(items: T[]): T[] {
    const query = this.search().trim().toLocaleLowerCase();
    return [...items]
      .filter((item) => item.name.toLocaleLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }
}
